import { useMemo } from "react";
import {
  analemmaPoints,
  dayIndex,
  eotZeroCrossings,
  equationOfTimeMinutes,
  nextEotZero,
  solarGeometry,
} from "@/lib/solar";
import { H, W } from "@/components/analemma/scale";
import AnalemmaAxes from "@/components/analemma/AnalemmaAxes";
import { MonthMarkers, SyncMarker, TodayMarker, curvePath } from "@/components/analemma/AnalemmaMarkers";
import { SyncCalendar, SyncCaption } from "@/components/analemma/SyncCalendar";

// Analemma: declination vs equation of time across the year, with today and the next EoT = 0 marked.
export default function Analemma({ now }: { now: Date }) {
  const year = now.getFullYear();
  const month = now.getMonth();
  const date = now.getDate();

  const points = useMemo(() => analemmaPoints(year), [year]);
  const path = useMemo(() => curvePath(points), [points]);
  const yearSyncs = useMemo(() => eotZeroCrossings(year), [year]);
  const sync = useMemo(() => nextEotZero(new Date(year, month, date, 12)), [year, month, date]);

  const today = points[Math.min(dayIndex(now), points.length - 1)];

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md" role="img" aria-label="Analemma: the sun's figure-eight pattern across one year">
        <AnalemmaAxes />
        <path d={path} fill="none" stroke="#c0501b" strokeWidth="1.4" strokeLinejoin="round" />
        <MonthMarkers points={points} year={year} />
        <TodayMarker point={today} eot={equationOfTimeMinutes(now)} dec={solarGeometry(now).declination} />
        {sync && <SyncMarker dec={solarGeometry(sync).declination} />}
      </svg>
      {sync && <SyncCaption date={sync} now={now} />}
      <SyncCalendar dates={yearSyncs} now={now} next={sync} />
    </div>
  );
}
